import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { PostgrestError, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { AuthUser, UserOrganization } from '../lib/auth.types';
import { signIn, signUp, signOut } from '../lib/auth.service';
import toast from 'react-hot-toast';

interface AuthContextType {
    session: Session | null;
    user: AuthUser | null;
    signIn: (email: string, password: string) => Promise<void>;
    signUp: (email: string, password: string, fullName: string) => Promise<void>;
    signOut: () => Promise<void>;
    loading: boolean;
    fetchUserProfile: (userId: string) => Promise<void>;
    isAuthenticated: boolean; // Added for easier auth state checking
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [session, setSession] = useState<Session | null>(null);
    const [user, setUser] = useState<AuthUser | null>(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false); // Added for easier auth state checking
    const hasInitialized = useRef(false); // Flag to track if the component has been initialized

    useEffect(() => {
        if (hasInitialized.current || typeof window === 'undefined') return;
        hasInitialized.current = true;

        console.log('AuthProvider initialized');

        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
            console.log('Auth state changed:', event, session?.user?.email || 'No user');
            console.log('Session:', session);
            setSession(session);

            if (session?.user) {
                fetchUserProfile(session.user.id).catch((err) => {
                    console.error('Failed to fetch user profile on hydration:', err);
                });
                setIsAuthenticated(true);
            } else {
                setUser(null);
                setLoading(false);
                setIsAuthenticated(false);
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    const fetchUserProfile = async (userId: string) => {
        try {
            console.log('Fetching user profile for:', userId);
            setLoading(true);

            const { user, error } = await fetchUserWithOrgs(userId);

            if (error) {
                console.error('Error fetching user profile:', error);

                // If profile doesn't exist, create it
                if (error.code === 'PGRST116') {
                    console.log('User profile not found, creating new profile');
                    const { data: authUser } = await supabase.auth.getUser();
                    if (authUser?.user) {
                        try {
                            console.log('Creating user profile for:', userId);
                            const { error: createError } = await supabase
                                .from('users')
                                .insert({
                                    id: userId,
                                    email: authUser.user.email || '',
                                    full_name: authUser.user.user_metadata?.full_name || authUser.user.email?.split('@')[0] || 'User'
                                });

                            if (createError) {
                                console.error('Error creating user profile:', createError);
                                toast.error('Failed to create user profile');
                            } else {
                                console.log('User profile created successfully');
                            }

                            // Fetch the newly created profile
                            console.log('Fetching newly created profile');
                            const { user: newProfile, error: fetchError } = await fetchUserWithOrgs(userId);

                            if (fetchError) {
                                console.error('Error fetching new profile:', fetchError);
                                toast.error('Failed to load user profile');
                                setLoading(false);
                                return;
                            }

                            console.log('New profile fetched successfully');
                            setUser(newProfile);
                            setIsAuthenticated(true);
                            setLoading(false);
                            return;
                        } catch (err) {
                            console.error('Error in profile creation process:', err);
                            toast.error('Failed to set up user profile');
                            setLoading(false);
                            return;
                        }
                    }
                }

                toast.error('Failed to load user profile');
                setLoading(false);
                return;
            }

            console.log('User profile fetched successfully');
            setUser(user);
            setIsAuthenticated(!!user);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching user profile:', error);
            toast.error('Failed to load user profile');
            setLoading(false);
        }
    };

    const fetchUserWithOrgs = async (userId: any): Promise<{ user: AuthUser | null, error: PostgrestError | null }> => {
        // Fetch the user basic profile
        console.log('▶ Fetching user...');
        const { data: user, error: userError } = await supabase
            .from('users')
            .select('*')
            .eq('id', userId)
            .single<AuthUser>();

        console.log('✅ User fetched:', user);
        if (userError) {
            console.error('Error fetching user:', userError.message);
            return { user: null, error: userError };
        }

        // Fetch related organizations and roles
        console.log('▶ Fetching orgs...');
        const { data: orgs, error: orgError } = await supabase
            .from('user_organizations')
            .select(`
                    id,
                    organization:organizations(*),
                    role:user_roles(*)
                `)
            .eq('user_id', userId)
            .overrideTypes<UserOrganization[], { merge: false }>();

        console.log('✅ Orgs fetched:', orgs);
        if (orgError) {
            console.error('Error fetching user organizations:', orgError.message);
            return { user: { ...user, organizations: [] }, error: orgError };
        }

        return {
            user: {
                ...user,
                organizations: orgs || []
            },
            error: null
        };
    };

    const handleSignIn = async (email: string, password: string) => {
        try {
            console.log('Handling sign in for:', email);
            const { data } = await signIn(email, password);
            if (!data?.user) {
                throw new Error('Invalid email or password');
            }
            console.log('Sign in successful, fetching profile');
            await fetchUserProfile(data.user.id);
        } catch (error) {
            console.error('Sign in error:', error);
            throw error;
        }
    };

    const handleSignUp = async (email: string, password: string, fullName: string) => {
        try {
            console.log('Handling sign up for:', email);
            const { data } = await signUp(email, password, fullName);
            if (!data?.user) {
                throw new Error('Failed to create account');
            }
            console.log('Sign up successful, fetching profile');
            await fetchUserProfile(data.user.id);
        } catch (error) {
            console.error('Sign up error:', error);
            throw error;
        }
    };

    const handleSignOut = async () => {
        try {
            console.log('Handling sign out');
            await signOut();
            setUser(null);
            setSession(null);
            setIsAuthenticated(false);
            console.log('Sign out successful');
        } catch (error) {
            console.error('Sign out error:', error);
            throw error;
        }
    };

    const value = {
        session,
        user,
        signIn: handleSignIn,
        signUp: handleSignUp,
        signOut: handleSignOut,
        loading,
        fetchUserProfile,
        isAuthenticated,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}